import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import { API_URL } from '../../../environment';
import { Modal, Button, Form, Nav, Tab } from "react-bootstrap";

interface Group {
  id: number;
  nome: string;
  ordem: number;
  padrao?: boolean;
  id_funil_pai?: number | null;
  nome_funil?: string; // vindo da API
  fields?: Field[];
}

interface Field {
  id: number;
  id_grupo: number;
  label: string;
  tipo: string;
  opcoes?: string;
  obrigatorio: boolean;
  ordem: number;
  // Preenchimento com IA: `instrucao` e o texto que guia a IA e `modo_ia` diz COMO
  // ela responde. `modo_ia` null = "dado unico" (o padrao) -- e assim que o Eduardo
  // ja preencheu os campos na mao em `card_campos`.
  instrucao?: string | null;
  modo_ia?: string | null;
}

const CardBuilderAdmin = () => {
  const routes = all_routes;
  
  // --- Estados do Admin ---
  const [times, setTimes] = useState<{ id: string; nome: string }[]>([]);
  const [selectedTimeId, setSelectedTimeId] = useState<string>("");

  const [groups, setGroups] = useState<Group[]>([]);
  
  // Modals
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showFieldModal, setShowFieldModal] = useState(false);
  
  // Editing States
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [editingField, setEditingField] = useState<Field | null>(null);
  
  // Form States
  const [newGroupName, setNewGroupName] = useState("");
  const [isGlobalGroup, setIsGlobalGroup] = useState(false); 
  const [currentGroupId, setCurrentGroupId] = useState<number | null>(null); // For adding field to a specific group
  
  const [newFieldLabel, setNewFieldLabel] = useState("");
  const [newFieldType, setNewFieldType] = useState("text");
  const [newFieldOptions, setNewFieldOptions] = useState("");
  const [newFieldRequired, setNewFieldRequired] = useState(false);
  const [newFieldIA, setNewFieldIA] = useState(false);
  const [newFieldInstrucao, setNewFieldInstrucao] = useState("");
  // "" = dado unico, que vai para o banco como NULL (ver MODOS_IA)
  const [newFieldModoIa, setNewFieldModoIa] = useState("");

  const fieldTypes: { [key: string]: string } = {
    text: "Texto",
    number: "Número",
    date: "Data",
    currency: "Moeda",
    select: "Seleção",
    checklist: "Checklist",
    file: "Arquivo"
  };

  // Modos de preenchimento por IA. O valor "" e o "dado unico" e vai para o banco
  // como NULL -- nao como texto. E o que a tabela `card_campos` ja mostra: os
  // campos de resposta direta que o Eduardo preencheu (ano do acidente, funcao
  // exercida, ...) tem `instrucao` preenchida e `modo_ia` NULL; so a narrativa
  // ficou com 'sintese' e o agendamento com 'agenda'.
  const MODOS_IA: { valor: string; rotulo: string; ajuda: string }[] = [
    { valor: "", rotulo: "Dado único", ajuda: "Resposta direta, do jeito que o cliente informou." },
    { valor: "sintese", rotulo: "Síntese", ajuda: "A IA resume a conversa em um texto corrido." },
    { valor: "agenda", rotulo: "Agenda", ajuda: "Data e hora de um compromisso." },
  ];

  // --- Busca Times ao iniciar ---
  useEffect(() => {
    const fetchTimes = async () => {
      const id_grupo = localStorage.getItem("id_grupo");
      if (!id_grupo) return;
      try {
        const res = await fetch(`${API_URL}/pegartodostimes`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_grupo }),
        });
        if (res.ok) {
          const data = await res.json();
          setTimes(data);
          // if (data.length > 0) setSelectedTimeId(String(data[0].id));
        }
      } catch (err) {
        console.error("Erro ao buscar times:", err);
      }
    };
    fetchTimes();
  }, []);

  // --- Limpa e recarrega quando troca o time ---
  useEffect(() => {
    setGroups([]);
    if (selectedTimeId) {
      fetchGroups();
    }
  }, [selectedTimeId]);

  const fetchGroups = async () => {
    if (!selectedTimeId) return;
    try {
      // No funnelId param = get all groups
      // escopo=time: tela de EDIÇÃO — só os grupos do time selecionado. Sem isso
      // os grupos globais dos outros times do cliente entrariam na lista e
      // poderiam ser editados/apagados aqui (PUT/DELETE não checam dono).
      const res = await fetch(`${API_URL}/card/groups?id_cliente=${selectedTimeId}&escopo=time`);
      if (res.ok) {
        const groupsData: Group[] = await res.json();
        // Fetch fields for each group
        const groupsWithFields = await Promise.all(groupsData.map(async (g) => {
          const fieldsRes = await fetch(`${API_URL}/card/fields?id_grupo=${g.id}`);
          const fields = fieldsRes.ok ? await fieldsRes.json() : [];
          return { ...g, fields };
        }));
        setGroups(groupsWithFields);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveGroup = async () => {
    if (!newGroupName || !selectedTimeId) return;
    
    try {
      if (editingGroup) {
         // Update existing group
         const res = await fetch(`${API_URL}/card/groups/${editingGroup.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            nome: newGroupName,
            padrao: isGlobalGroup
          })
        });
        if (res.ok) {
           closeGroupModal();
           fetchGroups();
        }
      } else {
        // Create new group
        const res = await fetch(`${API_URL}/card/groups`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_funil_pai: null, // Always created empty initially
            id_cliente: selectedTimeId, // Usando o time selecionado
            nome: newGroupName,
            ordem: groups.length + 1,
            padrao: isGlobalGroup
          })
        });
        if (res.ok) {
          closeGroupModal();
          fetchGroups();
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const closeGroupModal = () => {
      setShowGroupModal(false);
      setNewGroupName("");
      setIsGlobalGroup(false);
      setEditingGroup(null);
  };

  const openEditGroupModal = (group: Group) => {
    setEditingGroup(group);
    setNewGroupName(group.nome);
    setIsGlobalGroup(!!group.padrao);
    setShowGroupModal(true);
  };

  const handleDeleteGroup = async (id: number) => {
    if (!window.confirm("Tem certeza que deseja excluir este grupo?")) return;
    try {
      const res = await fetch(`${API_URL}/card/groups/${id}`, { method: "DELETE" });
      if (res.ok) {
        fetchGroups();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveField = async () => {
    if ((!currentGroupId && !editingField) || !newFieldLabel || !newFieldType) return;
    try {
      if (editingField) {
         // Update existing field
         const res = await fetch(`${API_URL}/card/fields/${editingField.id}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              label: newFieldLabel,
              tipo: newFieldType,
              opcoes: (newFieldType === 'select' || newFieldType === 'checklist') ? newFieldOptions : null,
              obrigatorio: newFieldRequired,
              instrucao: newFieldIA ? (newFieldInstrucao.trim() || null) : null,
              modo_ia: newFieldIA ? (newFieldModoIa || null) : null
            })
         });
         if (res.ok) {
            closeFieldModal();
            fetchGroups();
         }
      } else {
        // Create new field
        const currentGroup = groups.find(g => g.id === currentGroupId);
        const currentOrder = currentGroup?.fields?.length || 0;

        const res = await fetch(`${API_URL}/card/fields`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            id_grupo: currentGroupId,
            label: newFieldLabel,
            tipo: newFieldType,
            opcoes: newFieldType === 'select' ? newFieldOptions : null,
            obrigatorio: newFieldRequired,
            instrucao: newFieldIA ? (newFieldInstrucao.trim() || null) : null,
            modo_ia: newFieldIA ? (newFieldModoIa || null) : null,
            ordem: currentOrder + 1
          })
        });
        if (res.ok) {
          closeFieldModal();
          fetchGroups();
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const closeFieldModal = () => {
    setShowFieldModal(false);
    setNewFieldLabel("");
    setNewFieldType("text");
    setNewFieldOptions("");
    setNewFieldRequired(false);
    setNewFieldIA(false);
    setNewFieldInstrucao("");
    setNewFieldModoIa("");
    setEditingField(null);
  };

  const handleDeleteField = async (id: number) => {
    if (!window.confirm("Tem certeza que deseja excluir este campo?")) return;
    try {
      const res = await fetch(`${API_URL}/card/fields/${id}`, { method: "DELETE" });
      if (res.ok) {
        fetchGroups();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const openCreateFieldModal = (groupId: number) => {
    setCurrentGroupId(groupId);
    setEditingField(null);
    setNewFieldLabel("");
    setNewFieldType("text");
    setNewFieldOptions("");
    setNewFieldRequired(false);
    setNewFieldIA(false);
    setNewFieldInstrucao("");
    setNewFieldModoIa("");
    setShowFieldModal(true);
  };

  const openEditFieldModal = (field: Field) => {
    setEditingField(field);
    setCurrentGroupId(field.id_grupo);
    setNewFieldLabel(field.label);
    setNewFieldType(field.tipo);
    setNewFieldOptions(field.opcoes || "");
    setNewFieldRequired(field.obrigatorio);
    // Vem marcado se o campo ja tem instrucao OU modo_ia -- um campo de "dado unico"
    // guarda modo_ia NULL, entao olhar so o modo_ia deixaria a caixa desmarcada.
    setNewFieldIA(!!(field.instrucao || field.modo_ia));
    setNewFieldInstrucao(field.instrucao || "");
    setNewFieldModoIa(field.modo_ia || "");
    setShowFieldModal(true);
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header mb-4">
          <div className="row align-items-center">
            <div className="col">
              <h4 className="page-title mb-1 text-dark fw-bold">Configurador de Perfil (Cards) — Admin</h4>
              <p className="text-muted fs-13 mb-0">
                <i className="ti ti-shield-check me-1"></i>
                Gerencie os campos personalizados de qualquer time do sistema.
              </p>
            </div>
            <div className="col-auto d-flex align-items-center gap-3">
              <select
                className="form-select w-auto"
                value={selectedTimeId}
                onChange={(e) => setSelectedTimeId(e.target.value)}
              >
                <option value="">-- Selecione um time --</option>
                {times.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.nome}
                  </option>
                ))}
              </select>
              {selectedTimeId && (
                <button
                  className="btn btn-primary d-flex align-items-center gap-2 rounded-pill px-4 shadow-sm"
                  onClick={() => {
                    setNewGroupName("");
                    setIsGlobalGroup(false);
                    setEditingGroup(null);
                    setShowGroupModal(true);
                  }}
                >
                  <i className="ti ti-plus fs-16" /> <span>Novo Grupo</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {!selectedTimeId ? (
          <div className="text-center py-5 rounded-4 border-dashed mx-0 card-builder-empty">
            <i className="ti ti-users-group fs-40 text-muted mb-3"></i>
            <h5>Selecione um time</h5>
            <p className="text-muted small">Escolha um time acima para visualizar e gerenciar seus grupos de campos.</p>
          </div>
        ) : groups.length === 0 ? (
          <div className="text-center py-5 rounded-4 border-dashed mx-0 card-builder-empty">
            <i className="ti ti-layout-board-split fs-40 text-muted mb-3"></i>
            <h5>Nenhum grupo criado ainda</h5>
            <p className="text-muted small">Crie seu primeiro grupo para começar a adicionar campos personalizados.</p>
            <Button variant="primary" className="rounded-pill px-4" onClick={() => setShowGroupModal(true)}>
              Criar Primeiro Grupo
            </Button>
          </div>
        ) : (
          <div className="card-builder-vertical-container">
            <Tab.Container id="builder-admin-tabs" defaultActiveKey={groups[0]?.id}>
              <div className="row g-0 border rounded-4 overflow-hidden shadow-sm card-builder-container" style={{ minHeight: window.innerWidth < 768 ? 'auto' : '600px' }}>

                {/* Sidebar de Grupos */}
                <div className="col-12 col-md-3 border-end card-builder-sidebar">
                  <div className="p-3 border-bottom d-flex justify-content-between align-items-center card-builder-sidebar-header">
                    <span className="fs-11 fw-bold text-uppercase text-muted letter-spacing-1">Grupos de Dados</span>
                    <span className="badge bg-soft-secondary text-secondary rounded-pill">{groups.length}</span>
                  </div>
                  <Nav variant="pills" className="flex-column p-2 gap-1 custom-nav-pills builder-sidebar-nav">
                    {groups.map(group => (
                      <Nav.Item key={group.id}>
                        <Nav.Link
                          eventKey={group.id}
                          className="d-flex align-items-center justify-content-between border-0 rounded-3 py-2 px-3 fs-13 transition-all"
                        >
                          <div className="d-flex align-items-center gap-2 overflow-hidden">
                            <i className={`ti ${group.padrao ? 'ti-world text-primary' : 'ti-category text-muted'} fs-16`}></i>
                            <span className="text-truncate fw-medium">{group.nome}</span>
                          </div>
                          {group.padrao && <i className="ti ti-lock-check fs-10 text-primary-light" title="Global"></i>}
                        </Nav.Link>
                      </Nav.Item>
                    ))}
                  </Nav>
                </div>

                {/* Área de Edição dos Campos */}
                <div className="col-12 col-md-9 card-builder-content">
                  <Tab.Content className="h-100">
                    {groups.map(group => (
                      <Tab.Pane key={group.id} eventKey={group.id} className="h-100 animate__animated animate__fadeIn animate__faster">
                        <div className="p-4 d-flex flex-column h-100">

                          {/* Header do Grupo Selecionado */}
                          <div className="d-flex justify-content-between align-items-center mb-4 pb-3 border-bottom">
                            <div>
                              <div className="d-flex align-items-center gap-2 mb-1">
                                <h5 className="mb-0 fw-bold text-dark">{group.nome}</h5>
                                {group.padrao && <span className="badge bg-soft-primary text-primary fs-10 rounded-pill px-2">GRUPO GLOBAL</span>}
                              </div>
                              <p className="text-muted small mb-0">
                                {group.fields?.length || 0} campos configurados neste grupo.
                              </p>
                            </div>
                            <div className="d-flex gap-2">
                              <button className="btn btn-outline-light text-dark border btn-sm d-flex align-items-center gap-1 rounded-pill px-3" onClick={() => openEditGroupModal(group)}>
                                <i className="ti ti-edit fs-14" /> Editar Grupo
                              </button>
                              <button className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1 rounded-pill px-3" onClick={() => handleDeleteGroup(group.id)}>
                                <i className="ti ti-trash fs-14" /> Excluir
                              </button>
                            </div>
                          </div>

                          {/* Lista de Campos - Compacta */}
                          <div className="flex-grow-1 overflow-auto pe-2" style={{ maxHeight: '480px' }}>
                            {group.fields && group.fields.length > 0 ? (
                              <div className="row g-2">
                                {group.fields.map(field => (
                                  <div key={field.id} className="col-12 col-sm-6 col-md-4 col-xl-3">
                                    <div className="card border shadow-none rounded-3 mb-0 h-100 hover-border-primary transition-all">
                                      <div className="card-body p-2">
                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                          <div className="avatar avatar-xs rounded-2 bg-soft-secondary text-secondary d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px' }}>
                                            <i className={`ti ${
                                              field.tipo === 'date' ? 'ti-calendar' :
                                              field.tipo === 'number' ? 'ti-hash' :
                                              field.tipo === 'currency' ? 'ti-coin' :
                                              field.tipo === 'select' ? 'ti-list-details' :
                                              field.tipo === 'checklist' ? 'ti-checkbox' :
                                              field.tipo === 'file' ? 'ti-file-upload' : 'ti-text-recognition'
                                            } fs-12`}></i>
                                          </div>
                                          <div className="d-flex">
                                            <button className="btn btn-icon btn-xs text-muted hover-text-primary p-0" onClick={() => openEditFieldModal(field)}>
                                              <i className="ti ti-edit fs-11" />
                                            </button>
                                            <button className="btn btn-icon btn-xs text-muted hover-text-danger p-0" onClick={() => handleDeleteField(field.id)}>
                                              <i className="ti ti-x fs-11" />
                                            </button>
                                          </div>
                                        </div>
                                        <h6 className="fs-12 fw-bold text-dark mb-0 text-truncate" title={field.label}>{field.label}</h6>
                                        <div className="d-flex align-items-center gap-1 mt-1">
                                          <span className="fs-10 text-muted text-uppercase letter-spacing-1 fw-medium">
                                            {fieldTypes[field.tipo] || field.tipo}
                                          </span>
                                          {field.obrigatorio && <i className="ti ti-asterisk text-danger fs-8" title="Obrigatório"></i>}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                                <div className="col-sm-6 col-md-4 col-xl-3">
                                  <div
                                    className="card border-dashed h-100 d-flex align-items-center justify-content-center p-2 cursor-pointer hover-bg-light transition-all"
                                    onClick={() => openCreateFieldModal(group.id)}
                                    style={{ minHeight: '75px' }}
                                  >
                                    <div className="text-center">
                                      <i className="ti ti-plus text-muted fs-16 mb-0"></i>
                                      <p className="mb-0 fs-11 fw-bold text-muted">Novo Campo</p>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              <div className="text-center py-5 border-dashed rounded-3">
                                <i className="ti ti-stack-push fs-32 text-muted mb-2"></i>
                                <h6 className="text-muted">Nenhum campo neste grupo</h6>
                                <Button variant="primary" size="sm" className="rounded-pill px-4 mt-2" onClick={() => openCreateFieldModal(group.id)}>
                                  Começar a Adicionar
                                </Button>
                              </div>
                            )}
                          </div>

                        </div>
                      </Tab.Pane>
                    ))}
                  </Tab.Content>
                </div>
              </div>
            </Tab.Container>
          </div>
        )}
      </div>

      {/* Modal Grupo - Estilizado */}
      <Modal show={showGroupModal} onHide={closeGroupModal} centered className="modern-modal">
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold">{editingGroup ? "Configurar Grupo" : "Criar Novo Grupo"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-4">
            <Form.Label className="fs-13 fw-bold text-muted mb-2">IDENTIFICAÇÃO</Form.Label>
            <Form.Control
              className="form-control-lg fs-15 border-2 shadow-none"
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="Ex: Dados Cadastrais, Veículo, etc."
            />
          </Form.Group>

          <div className="bg-light p-3 rounded-3 border">
            <Form.Check
              type="switch"
              id="check-global-admin"
              label={<span className="fw-bold">Tornar Grupo Global</span>}
              checked={isGlobalGroup}
              onChange={(e) => setIsGlobalGroup(e.target.checked)}
            />
            <p className="text-muted small mb-0 mt-1 ps-4">
              Grupos globais aparecem automaticamente em <strong>todos</strong> os funis do sistema.
            </p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" className="rounded-pill px-4 fw-medium" onClick={closeGroupModal}>Cancelar</Button>
          <Button variant="primary" className="rounded-pill px-4 fw-bold shadow-sm" onClick={handleSaveGroup}>
            {editingGroup ? "Salvar Alterações" : "Criar Grupo"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal Campo - Estilizado */}
      <Modal show={showFieldModal} onHide={closeFieldModal} centered className="modern-modal">
        <Modal.Header closeButton className="border-0">
          <Modal.Title className="fw-bold">{editingField ? "Editar Atributo" : "Novo Atributo"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-12">
              <Form.Group>
                <Form.Label className="fs-12 fw-bold text-muted mb-1">LABEL (O QUE O VENDEDOR VÊ)</Form.Label>
                <Form.Control
                  className="border-2 shadow-none"
                  type="text"
                  value={newFieldLabel}
                  onChange={(e) => setNewFieldLabel(e.target.value)}
                  placeholder="Ex: Qual o modelo do carro?"
                />
              </Form.Group>
            </div>

            <div className="col-md-7">
              <Form.Group>
                <Form.Label className="fs-12 fw-bold text-muted mb-1">TIPO DE DADO</Form.Label>
                <Form.Select className="border-2 shadow-none" value={newFieldType} onChange={(e) => setNewFieldType(e.target.value)}>
                  <option value="text">Texto</option>
                  <option value="number">Número</option>
                  <option value="date">Data</option>
                  <option value="currency">Moeda ($)</option>
                  <option value="select">Seleção (Dropdown)</option>
                  <option value="checklist">Checklist (várias opções)</option>
                  <option value="file">Anexo / Arquivo</option>
                </Form.Select>
              </Form.Group>
            </div>

            <div className="col-md-5 d-flex align-items-end">
              <div className="mb-2">
                <Form.Check
                  type="checkbox"
                  id="check-required-admin"
                  label={<span className="fw-bold fs-13">Obrigatório?</span>}
                  checked={newFieldRequired}
                  onChange={(e) => setNewFieldRequired(e.target.checked)}
                />
              </div>
            </div>

            <div className="col-12">
              <Form.Check
                type="checkbox"
                id="check-ia-admin"
                label={<span className="fw-bold fs-13">Preenchimento com IA</span>}
                checked={newFieldIA}
                onChange={(e) => setNewFieldIA(e.target.checked)}
              />
            </div>

            {newFieldIA && (
              <div className="col-12">
                <div className="bg-light p-3 rounded-3 border animate__animated animate__fadeInDown">
                  <Form.Group className="mb-3">
                    <Form.Label className="fs-12 fw-bold text-muted mb-1">INSTRUÇÃO</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      className="border-2 shadow-none"
                      value={newFieldInstrucao}
                      onChange={(e) => setNewFieldInstrucao(e.target.value)}
                      placeholder="Ex: Registre apenas o ano em que ocorreu o acidente, informado pelo segurado."
                    />
                    <Form.Text className="text-muted fs-11 mt-2 d-block">
                      O que a IA deve escrever neste campo.
                    </Form.Text>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label className="fs-12 fw-bold text-muted mb-1">MODO_IA</Form.Label>
                    <Form.Select
                      className="border-2 shadow-none"
                      value={newFieldModoIa}
                      onChange={(e) => setNewFieldModoIa(e.target.value)}
                    >
                      {MODOS_IA.map((m) => (
                        <option key={m.valor || "unico"} value={m.valor}>{m.rotulo}</option>
                      ))}
                    </Form.Select>
                    <Form.Text className="text-muted fs-11 mt-2 d-block">
                      {MODOS_IA.find((m) => m.valor === newFieldModoIa)?.ajuda}
                    </Form.Text>
                  </Form.Group>
                </div>
              </div>
            )}

            {(newFieldType === "select" || newFieldType === "checklist") && (
              <div className="col-12">
                <div className="bg-light p-3 rounded-3 border animate__animated animate__fadeInDown">
                  <Form.Group>
                    <Form.Label className="fs-12 fw-bold text-muted mb-1">
                      {newFieldType === "checklist" ? "ITENS DA CHECKLIST" : "OPÇÕES DO DROPDOWN"}
                    </Form.Label>
                    <Form.Control
                      className="border-2 shadow-none"
                      type="text"
                      value={newFieldOptions}
                      onChange={(e) => setNewFieldOptions(e.target.value)}
                      placeholder="Opção 1, Opção 2, Opção 3"
                    />
                    <Form.Text className="text-muted fs-11 mt-2 d-block">
                      {newFieldType === "checklist"
                        ? "Cada item vira uma caixa de marcar. Separe usando vírgulas."
                        : "Separe as opções usando vírgulas."}
                    </Form.Text>
                  </Form.Group>
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="border-0">
          <Button variant="light" className="rounded-pill px-4 fw-medium" onClick={closeFieldModal}>Cancelar</Button>
          <Button variant="primary" className="rounded-pill px-4 fw-bold shadow-sm" onClick={handleSaveField}>
            {editingField ? "Salvar" : "Adicionar"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CardBuilderAdmin;